import { Col, Row } from 'antd';
import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useUploadContext } from 'components/FileUploader';
import { Button } from 'components/ui';
import {
  type AtlasGqlInspectionType,
  type AtlasGqlMetadataField,
  type AtlasGqlMetadataFieldMapping,
} from 'types/atlas-graphql';

import { ConfirmationWarnings } from '../../InspectionDetail/InspectionDamageIngestion/ModalContent/Confirmation/ConfirmationWarnings';
import {
  DATE_VALIDATION_WARNING_MSG,
  isFutureDate,
  isValidDateFormat,
} from '../../InspectionDetail/InspectionDamageIngestion/ModalContent/Confirmation/utils';
import { useUploadInspectionDataModalContext } from '../Context/UploadInspectionDataModalContext';
import { Steps } from '../UploadInspectionDataModal';
import { ButtonContainer, LabelColumn, WarningContainer } from '../UploadInspectionDataModal.style';

type PreviewProps = {
  inspectionCount: number;
  inspectionType?: AtlasGqlInspectionType;
  internalFields: AtlasGqlMetadataField[];
  fieldMappings: AtlasGqlMetadataFieldMapping[];
  records: { [key: string]: string }[];
  setStep: Dispatch<SetStateAction<Steps>>;
  handleComplete: () => void;
};

export const Preview: React.FC<PreviewProps> = ({
  inspectionCount,
  inspectionType,
  internalFields,
  fieldMappings,
  records,
  setStep,
  handleComplete,
}) => {
  const { uploading } = useUploadContext();
  const { setAlertState } = useUploadInspectionDataModalContext();
  const { t } = useTranslation();

  const inspectionTypeFieldName = fieldMappings.find(fieldMapping => {
    const internalTypeField = internalFields.find(field => field.displayName === 'Inspection Type');
    return fieldMapping.targetFieldName === internalTypeField?.internalName;
  });
  const inspectionTypes = inspectionTypeFieldName
    ? records.map(record => record[inspectionTypeFieldName.sourceFieldName])
    : [];
  const isValid = inspectionType?.name
    ? inspectionTypes.every(type => inspectionType?.name === type)
    : inspectionTypes.every(type => inspectionTypes[0] === type);
  const previewRecord = records[0];

  const inspectionDateFieldName = fieldMappings.find(fieldMapping => {
    const internalTypeField = internalFields.find(field => field.displayName === 'Inspection Date');
    return fieldMapping.targetFieldName === internalTypeField?.internalName;
  });
  const inspectionDates = inspectionDateFieldName
    ? records.map(record => record[inspectionDateFieldName.sourceFieldName])
    : [];
  const isDateError =
    !!inspectionDates?.length &&
    inspectionDates.some(date => !date?.trim() || !isValidDateFormat(date));
  const futureDateCount = inspectionDates.filter(
    date => isValidDateFormat(date) && isFutureDate(date)
  ).length;
  const warnings = [
    ...(isDateError ? [DATE_VALIDATION_WARNING_MSG] : []),
    ...(futureDateCount > 0
      ? [
          t(
            'inspection.futureDateValidationWarning',
            '{{count}} Inspections have dates in the future. If this is not intentional, please correct these to prevent issues in Horizon.',
            { count: futureDateCount }
          ),
        ]
      : []),
  ];

  useEffect(() => {
    if (!isValid) {
      setAlertState({
        alertTitle: `All Inspections must be of the same type ${inspectionType?.name ? `: ${inspectionType?.name}` : '.'}`,
        alertType: 'error',
        alertDescription: 'Please use the button below to upload again.',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid]);

  const goBack = (step: Steps) => {
    setAlertState(null);
    setStep(step);
  };

  return (
    <>
      {isValid && (
        <>
          <p>{t('inspection.willCreateInspections', { count: inspectionCount })}</p>
          <p>
            {t(
              'inspection.previewFirstUploadedInspection',
              'Preview of the first uploaded inspection:'
            )}
          </p>
          {fieldMappings.map(({ sourceFieldName, targetFieldName }) => (
            <Row key={targetFieldName}>
              <Col flex="10%" />
              <LabelColumn flex="auto">
                <label>
                  {internalFields.find(field => field.internalName === targetFieldName)
                    ?.displayName ?? targetFieldName}
                  :
                </label>
              </LabelColumn>
              <Col flex="60%">{previewRecord?.[sourceFieldName]}</Col>
            </Row>
          ))}
          <WarningContainer>
            <ConfirmationWarnings warnings={warnings} />
          </WarningContainer>
        </>
      )}
      <ButtonContainer>
        {isValid ? (
          <>
            <Button
              onClick={() => {
                goBack(Steps.SummaryMapping);
              }}
              disabled={uploading}
            >
              {t('common.back', 'Back')}
            </Button>
            <Button
              type="primary"
              onClick={() => {
                setAlertState(null);
                handleComplete();
              }}
              loading={uploading}
            >
              {warnings.length > 0
                ? t('inspection.ignoreWarningContinue', 'Ignore the warning, continue')
                : t('common.continue', 'Continue')}
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={() => {
                goBack(Steps.SummaryUpload);
              }}
            >
              {t('components.reupload', 'Reupload')}
            </Button>
          </>
        )}
      </ButtonContainer>
    </>
  );
};
